<template>
  <div class="user-management-container">
    <div class="user-management">
      <Sticky
        v-if="showUserManagementEditBtn"
        :fullWidth="false"
        :zIndex="Number(101)"
        :align="'right'"
        class="sticky-btns"
        extendStickyClass="sticky-state"
      >
        <HKLButton
          v-if="editing"
          class="no-min-width"
          action="cancel"
          color="darkblue"
          @click="cancelEdit"
        />
        <HKLButton
          v-if="editing"
          class="no-min-width"
          action="save"
          color="orange"
          @click="doSave"
        />
        <HKLButton
          v-if="!editing"
          class="no-min-width"
          action="edit"
          color="orange"
          @click="switchEditState"
        />
        <button
          v-if="(!editing || createProject) && isMaster"
          type="button"
          class="btn btn--orange"
          @click="createProject"
        >
          {{ $t('newProject') }}
        </button>
      </Sticky>
      <div class="user-management-content">
        <label>{{ $t('tenancy.masterTenantAccount') }}</label>
        <div class="master-account">
          <div class="name">
            <InputField
              v-for="fullnameObj of getFullnameSorts"
              :key="fullnameObj.key"
              :placeholder="$t(`contact.basic.${fullnameObj.label}`)"
              :showColon="false"
              :showFlag="fullnameObj.isFirst ? isEngineerEditing : false"
              :label="fullnameObj.isFirst ? $t('user.name') : 'lastname'"
              :value="masterAccount.TenancyAccount && masterAccount.TenancyAccount[`contact${fullnameObj.key}`] || ''"
              :readonly="!isEngineerEditing"
              :readonlyAsInput="(isEngineer && !allowUpdateMasterAccount) && editing"
              :required="true"
              @updateValue="val => changeMasterAccountName(`contact${fullnameObj.key}`, val)"
            />
          </div>
          <div class="account-email">
            <InputField
              :label="$t('user.accountEmail')"
              :showColon="false"
              :showFlag="isEngineerEditing"
              :value="masterAccount.username"
              :pattern="regex.email"
              :inputTip="errorEmail"
              :readonly="!isEngineerEditing"
              :readonlyAsInput="(isEngineer && !allowUpdateMasterAccount) && editing"
              :required="true"
              @updateValue="val => changeMasterAccountUsername(val)"
            />
          </div>
        </div>
        <div
          v-if="!editing"
          class="btns"
        >
          <button
            v-if="isEngineer && allowInviteMaster"
            class="invite-master-user btn btn--darkblue"
            @click="openMasterInviteModal"
          >
            {{ $t('tenancy.inviteMasterUser') }}
          </button>
          <button
            v-if="isMaster"
            class="reset-password btn btn--darkblue"
            @click="resetPassword"
          >
            {{ $t('tenancy.resetPassword') }}
          </button>
          <button
            v-if="canRequestForInvitation"
            v-hkldreset
            type="button"
            class="btn btn--darkblue edit"
            :class="{'tenant-edit': canRequestForInvitation }"
            @click="sendRequest()"
          >
            {{ $t('action.requestForInvitation') }}
          </button>
        </div>
      </div>
      <div class="show-all-users">
        <label>{{ $t('tender.tenancyUser') }} {{ isEngineer ? $t('tender.tenantMaintain') : '' }}</label>
        <table>
          <thead>
            <tr>
              <th
                v-for="(item, index) in filteredTableElements"
                :key="index"
              >
                {{ item.title.toUpperCase() }}
                <Popover
                  v-if="item.popover"
                  :text="item.popover"
                />
              </th>
              <th v-if="isTenant" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in allAccounts"
              :key="index"
            >
              <td class="username">
                <InputField
                  :showColon="false"
                  :showFlag="false"
                  :value="item.username"
                  :pattern="regex.email"
                  :inputTip="errorEmail"
                  :readonly="isAdmin(item) || !isUserTableEditing(item)"
                  :required="true"
                  @updateValue="val => changeSubAccountUserName(item, val)"
                />
              </td>
              <td class="name">
                <div v-if="!isUserTableEditing(item)">
                  {{ getFullName(item) }}
                </div>
                <div
                  v-else
                  class="editing-name"
                >
                  <InputField
                    v-for="fullnameObj of getFullnameSorts"
                    :key="fullnameObj.key"
                    :showColon="false"
                    :showFlag="false"
                    :value="item.TenancyAccount[`contact${fullnameObj.key}`]"
                    :readonly="isAdmin(item)"
                    :required="true"
                    :placeholder="$t(`contact.basic.${fullnameObj.label}`)"
                    @updateValue="val => changeSubAccountName(item, `contact${fullnameObj.key}`, val)"
                  />
                </div>
              </td>
              <td>
                <input
                  v-if="isAdmin(item)"
                  type="checkbox"
                  disabled="true"
                  :checked="isAdmin(item)"
                >
                <span v-else>-</span>
              </td>
              <td>
                <input
                  v-if="isUserTableEditing(item) || !!item.TenancyAccount.perms[TenancySubAccountPermKeys.SubAdmin]"
                  type="checkbox"
                  :disabled="isAdmin(item) || isEngineer || !isUserTableEditing(item)"
                  :checked="!!item.TenancyAccount.perms[TenancySubAccountPermKeys.SubAdmin]"
                  @change="() => changePerms(item, TenancySubAccountPermKeys.SubAdmin)"
                >
                <span v-else>-</span>
              </td>
              <td v-if="enableTenantProjectsFeature">
                <input
                  v-if="isUserTableEditing(item) || !!item.TenancyAccount.perms[TenancySubAccountPermKeys.TenantProject]"
                  type="checkbox"
                  :disabled="isAdmin(item) || isEngineer || !isUserTableEditing(item)"
                  :checked="!!item.TenancyAccount.perms[TenancySubAccountPermKeys.TenantProject]"
                  @change="() => changePerms(item, TenancySubAccountPermKeys.TenantProject)"
                >
                <span v-else>-</span>
              </td>
              <td v-if="enableTenantProjectsFeature">
                <input
                  v-if="isUserTableEditing(item) || !!item.TenancyAccount.perms[TenancySubAccountPermKeys.HandoverTakeback]"
                  type="checkbox"
                  :disabled="isAdmin(item) || isEngineer || !isUserTableEditing(item)"
                  :checked="!!item.TenancyAccount.perms[TenancySubAccountPermKeys.HandoverTakeback]"
                  @change="() => changePerms(item, TenancySubAccountPermKeys.HandoverTakeback)"
                >
                <span v-else>-</span>
              </td>
              <td v-if="enableWorkPermitsFeature">
                <input
                  v-if="isUserTableEditing(item) || !!item.TenancyAccount.perms[TenancySubAccountPermKeys.StandaloneWorkPermits]"
                  type="checkbox"
                  :disabled="isAdmin(item) || isEngineer || !isUserTableEditing(item)"
                  :checked="!!item.TenancyAccount.perms[TenancySubAccountPermKeys.StandaloneWorkPermits]"
                  @change="() => changePerms(item, TenancySubAccountPermKeys.StandaloneWorkPermits)"
                >
                <span v-else>-</span>
              </td>
              <td v-if="enableTenancyRecordsFeature">
                <input
                  v-if="isUserTableEditing(item) || !!item.TenancyAccount.perms[TenancySubAccountPermKeys.TenancyRecord]"
                  type="checkbox"
                  :disabled="isAdmin(item) || isEngineer || !isUserTableEditing(item)"
                  :checked="!!item.TenancyAccount.perms[TenancySubAccountPermKeys.TenancyRecord]"
                  @change="() => changePerms(item, TenancySubAccountPermKeys.TenancyRecord)"
                >
                <span v-else>-</span>
              </td>
              <td v-if="enableGreenOperationFeature">
                <input
                  v-if="isUserTableEditing(item) || !!item.TenancyAccount.perms[TenancySubAccountPermKeys.GreenOperation]"
                  type="checkbox"
                  :disabled="isAdmin(item) || isEngineer || !isUserTableEditing(item)"
                  :checked="!!item.TenancyAccount.perms[TenancySubAccountPermKeys.GreenOperation]"
                  @change="() => changePerms(item, TenancySubAccountPermKeys.GreenOperation)"
                >
                <span v-else>-</span>
              </td>
              <td class="lastLoginAt">
                {{ formatDate(item.lastLoginAt) }}
              </td>
              <td class="invitedAt">
                <div>{{ formatDate(item.TenancyAccount.invitedAt) || '-' }}</div>
                <i
                  v-if="!isAdmin(item) && isUserTableEditing(item)"
                  class="fa fa-times fa-lg"
                  @click="showDeleteModal(index)"
                />
              </td>
              <td v-if="isMaster || (isTenancySubAdmin && !isCurrentUser(item))">
                <button
                  v-if="!editing && !isAdmin(item)"
                  class="invite btn btn--darkblue"
                  @click="inviteSubAccount(item)"
                >
                  {{ $t('action.invite') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <i
          v-if="isUserTableEditing()"
          class="addBtn"
          @click="addNewSubTenancyUser()"
        >
          {{ $t('userManagement.addNewTenancyUser') }}
        </i>
      </div>
      <TenancyContractors
        v-if="enableWorkPermitsFeature"
        :isEditing="editing"
        :hasSWPRight="hasTenantSwpRight"
      />
      <RepresentativeVue v-if="isHKlandUser" />
      <modal ref="deleteUserManagementCommitModal">
        <div slot="content">
          <p class="modal-pure-text">
            {{ $t('userManagement.deleteUserTips') }}
          </p>
        </div>
        <div slot="btns">
          <button
            class="cancel btn btn--darkblue"
            @click="closeDeleteModal"
          >
            {{ $t('action.cancel') }}
          </button>
          <button
            class="comfirm btn btn--orange"
            @click="deleteAccount"
          >
            {{ $t('action.delete') }}
          </button>
        </div>
      </modal>
    </div>
    <TenantsInviteModalVue
      ref="tenantsInviteModal"
    />
    <MasterInviteModal
      ref="masterInviteModal"
      :masterAccount="masterAccount"
      @inviteClick="doInviteMasterAccount"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { computed, getCurrentInstance, unref } from 'vue-demi'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { sendEmail } from '@/api/postman/request'
import * as tenanciesApi from '@/api/tenancies/request'
import HKLButton from '@/controls/Button/HKLButton.vue'
import InputField from '@/controls/InputField'
import Modal from '@/controls/Modal'
import { useRefModal } from '@/controls/Modal/modal.hook'
import Popover from '@/controls/Popover'
import Sticky from '@/controls/Sticky'
import TenancyContractors from '@/modules/ccd/tenancy/contractors/index.vue'
import RepresentativeVue from '@/modules/ccd/tenancy/representative/index.vue'
import formFullnameMixin from '@/pages/formFullnameMixin'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import { useRouteParamId } from '@/router/useRouter'
import { useStoreActions, useStoreGetters } from '@/store'
import mutationTypes from '@/store/mutation-types'
import { TenancySubAccountPermKeys, TenancySubAccountPerms, aclHelper, ccdPermsTypes } from '@/utils/aclHelper'
import { EmailTypes } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import EventBus from '@/utils/eventBus'
import i18n from '@/utils/i18n'
import notification from '@/utils/notification'
import * as regex from '@/utils/regex'
import MasterInviteModal from './MasterInviteModal.vue'
import TenantsInviteModalVue from './TenantsInviteModal.vue'
import { useHasTenantSwpRight, useIsTenancySubAdmin, useNeedInvitedStandaloneContractors, useNeedInvitedSubAccounts } from './tenancy.hook'
import { activateSubAccountAndSendEmail } from './tenancy.utils'

const accountModel = {
  TenancyAccount: {
    contactFirstName: '',
    contactLastName: '',
    perms: {},
    updatedAt: '',
    invitedAt: '',
  },
  username: '',
}
export default {
  components: {
    HKLButton,
    Sticky,
    InputField,
    Modal,
    Popover,
    TenancyContractors,
    RepresentativeVue,
    TenantsInviteModalVue,
    MasterInviteModal,
  },
  mixins: [formFullnameMixin],
  props: {
    isMaster: Boolean,
    allowInviteMaster: Boolean,
    allowUpdateMasterAccount: Boolean,
    isEdit: {
      type: Boolean,
      default: false,
    },
    createProject: {
      type: [String, Function],
      default: '',
    },
  },
  setup () {
    const { proxy: that } = getCurrentInstance()
    const tenancyId = useRouteParamId()
    const { validationResult: contractorsValidationResult, sawpContractors } = useStoreGetters('tenancyContractors', ['validationResult', 'sawpContractors'])
    const { isHKlandUser } = useStoreGetters('user', ['isHKlandUser'])
    const { accounts } = useStoreGetters('ccd', ['accounts'])
    const { uploadTenancySubAccounts } = useStoreActions('ccd', ['uploadTenancySubAccounts'])
    const { modal: tenantsInviteModal, open: openTenantsInviteModal } = useRefModal()
    const needInvitedStandaloneContractors = useNeedInvitedStandaloneContractors(sawpContractors)
    const needInvitedSubAccounts = useNeedInvitedSubAccounts(accounts)
    const shouldShowTenantsInviteModal = computed(() => {
      return unref(needInvitedStandaloneContractors).length || unref(needInvitedSubAccounts).length
    })
    const {
      modal: masterInviteModal,
      open: openMasterInviteModal,
      close: closeMasterInviteModal,
    } = useRefModal()
    function validateTenancyUserEmails () {
      const nodes = $('.show-all-users .username')
      const saNodes = $('.table-container .user-account-input')
      nodes.removeClass('error-highlight')
      saNodes.removeClass('error-highlight')
      let isError = false
      let errorUsername = []
      // TODO replace that.accounts with accounts when updating i18n
      for (let i = 0, len = that.accounts.length; i < len; i++) {
        for (let j = i + 1; j < len; j++) {
          if (lodash.isEqual(that.accounts[i].username, that.accounts[j].username)) {
            if (!errorUsername.includes(that.accounts[i].username)) {
              errorUsername.push(that.accounts[i].username)
            }
            $(nodes[i]).addClass('error-highlight')
            $(nodes[j]).addClass('error-highlight')
            isError = true
          }
        }
      }
      const tenancyContractorsNames = that.tenancyContractors.map(tenancyContractors => tenancyContractors.userAccount)
      tenancyContractorsNames.forEach(userName => {
        if (userName && tenancyContractorsNames.indexOf(userName) !== tenancyContractorsNames.lastIndexOf(userName)) {
          errorUsername.push(userName)
          $(saNodes[tenancyContractorsNames.indexOf(userName)]).addClass('error-highlight')
          $(saNodes[tenancyContractorsNames.lastIndexOf(userName)]).addClass('error-highlight')
          isError = true
        }
      })
      errorUsername.map(errName => {
        notification.fireNotification(false, `${errName} is already in the list`)
      })
      return isError
    }
    function validateForAccounts () {
      // Check all fields of contractors input
      that.validateContractors()
      if (!unref(contractorsValidationResult)) return false

      fusionInputCheckUtil.validateAllFields(undefined, '.show-all-users')
      const allUserValid = fusionInputCheckUtil.isAllFieldsValid('.show-all-users')
      if (!allUserValid) return false
      if (validateTenancyUserEmails()) return false
      return true
    }
    function switchEditState () {
      that.editing = !that.editing
      if (that.editing) {
        EventBus.$emit('standaloneContractors.refresh')
      }
    }
    async function saveAllAccounts () {
      that.editing = false
      await Promise.all([
        uploadTenancySubAccounts({tenancyId: unref(tenancyId), accounts: that.allAccounts}),
        tenanciesApi.updateTenancyContractors(unref(tenancyId), that.tenancyContractors),
      ])
      await that.loadTenancy(unref(tenancyId))
      EventBus.$emit('standaloneContractors.refresh')
      formUtils.resetDirty()
    }
    async function cancelEdit () {
      switchEditState()
      EventBus.emit('standaloneContractors.discard')
      const nodes = $('.show-all-users .username')
      nodes.removeClass('error-highlight')
      await that.loadTenancy(unref(tenancyId))
      formUtils.resetDirty()
    }
    const hasTenantSwpRight = useHasTenantSwpRight()
    const isTenancySubAdmin = useIsTenancySubAdmin()
    const showUserManagementEditBtn = computed(() => {
      return unref(hasTenantSwpRight) || unref(isTenancySubAdmin)
    })
    const isCurrentUser = (item) => {
      return lodash.get(item, 'id') === that.currentUser.id
    }
    const isUserTableEditing = (item) => {
      if (!that.editing) return false
      if (that.isMaster) return true
      if (unref(isTenancySubAdmin)) return !isCurrentUser(item)
      return false
    }
    return {
      isHKlandUser,
      masterInviteModal,
      openMasterInviteModal,
      closeMasterInviteModal,
      switchEditState,
      cancelEdit,
      tenancyId,
      tenantsInviteModal,
      saveAllAccounts,
      hasTenantSwpRight,
      showUserManagementEditBtn,
      isTenancySubAdmin,
      isCurrentUser,
      isUserTableEditing,
      doSave: async () => {
        if (!validateForAccounts()) return
        await saveAllAccounts()
        if (unref(shouldShowTenantsInviteModal)) {
          openTenantsInviteModal()
        }
      },
      TenancySubAccountPermKeys: computed(() => TenancySubAccountPermKeys),
    }
  },
  data () {
    return {
      companyId: '',
      editing: false,
      regex,
      deleteUserManagementCommitModal: undefined,
      masterAccount: lodash.set(Object.assign({}, lodash.cloneDeep(accountModel)), 'TenancyAccount.isAdmin', true),
      editIndex: -1,
      errorEmail: i18n.t('hint.inputTip.email'),
    }
  },
  computed: {
    ...mapGetters('ccd', ['accounts', 'currentTenancy']),
    ...mapGetters('user', ['isEngineer', 'isTenant', 'currentUser']),
    ...mapState('tenancyContractors', {
      tenancyContractors: state => state.contractors,
    }),
    ...mapGetters('regions', ['enableWorkPermitsFeature', 'enableTenancyRecordsFeature', 'enableTenantProjectsFeature', 'enableGreenOperationFeature']),
    canRequestForInvitation () {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.requestForInvitation)
    },
    tableElements () {
      return [{
        title: i18n.t('tender.userAccount'),
        key: 'username',
      }, {
        title: i18n.t('user.name'),
        key: 'name',
      }, {
        title: i18n.t('tender.isMasterAccount'),
        key: 'isAdmin',
      }, {
        title: i18n.t('tender.manageSubAccount'),
        key: 'SubUserManage',
      }, {
        title: i18n.t('tender.fitoutAndReinstatementProjects'),
        key: 'TenantProject',
      }, {
        title: i18n.t('tender.handoverTakeback'),
        key: 'HandoverTakeback',
      }, {
        title: i18n.t('tender.standaloneWorkPermits'),
        key: 'StandaloneWorkPermits',
      }, {
        title: i18n.t('tender.adminTenancyRecord'),
        key: 'TenancyRecord',
      }, {
        title: i18n.t('tender.greenOperation'),
        key: 'GreenOperation',
      }, {
      //   TODO: @Jackson_Wang Uncomment the code when customer needs it
      //   title: i18n.t('tender.revECircular')\
      //   key: 'revECircular'
      // }, {
        title: i18n.t('lastLogin'),
        key: 'updatedAt',
      }, {
        title: i18n.t('tender.lastInviteDateTime'),
        key: 'invitedAt',
      }]
    },
    filteredTableElements () {
      return this.tableElements.filter(item => {
        switch (item.key) {
          case 'HandoverTakeback':
          case 'TenantProject':
            return this.enableTenantProjectsFeature

          case 'StandaloneWorkPermits':
            return this.enableWorkPermitsFeature

          case 'TenancyRecord':
            return this.enableTenancyRecordsFeature

          case 'GreenOperation':
            return this.enableGreenOperationFeature
          default:
            return true
        }
      })
    },
    isEngineerEditing () {
      return this.isEngineer && this.editing
    },
    isUpdateMasterEditing () {
      return this.allowUpdateMasterAccount && this.editing
    },
    allAccounts () {
      let adminIndex = -1
      let tmpAccounts = lodash.cloneDeep(this.accounts)
      if (!lodash.isEmpty(tmpAccounts)) {
        let admin = {}
        for (let i = 0, len = tmpAccounts.length; i < len; i++) {
          if (lodash.get(tmpAccounts[i], 'TenancyAccount.isAdmin', false)) {
            admin = tmpAccounts[i]
            adminIndex = i
            tmpAccounts.splice(i, 1)
            break
          }
        }
        if (adminIndex !== -1) {
          this.masterAccount = lodash.cloneDeep(admin)
        }

        if (adminIndex > 0) {
          tmpAccounts = lodash.sortBy(tmpAccounts, user => {
            return lodash.get(user, 'TenancyAccount.contactFirstName')
          })
        }
        tmpAccounts.unshift(admin)
        return tmpAccounts
      }
      return []
    },
    currentCompany () {
      return lodash.get(this.currentTenancy, 'company')
    },
    hasPremisesDetailList () {
      return this.currentTenancy.premisesDetailList.length > 0
    },
  },
  watch: {
    isEdit (val) {
      this.editing = val
    },
  },
  async mounted () {
    this.editing = this.isEdit
    await this.initModal()
  },
  methods: {
    ...mapActions('ccd', ['loadTenancy', 'uploadTenancyMasterAccount']),
    ...mapMutations('ccd', {
      setCurrentCompany: mutationTypes.COMPANY_UPDATE_CURRENT,
      updateTenancyAccounts: mutationTypes.TENANCY_UPDATE_ACCOUNTS,
      updateTenancyPathField: mutationTypes.TENANCY_UPDATE_PATH_FIELD,
    }),
    ...mapMutations('tenancyContractors', {
      validateContractors: mutationTypes.TENANCY_CONTRACTOR_VALIDATE,
    }),
    // Use in the saveAllTabs method of the src/pages/ccd/TenancyDetail.vue
    validate () {
      fusionInputCheckUtil.validateAllFields(undefined, '.user-management-content')
      return fusionInputCheckUtil.isAllFieldsValid('.user-management-content')
    },
    formatDate (time) {
      return time ? dateUtil.format(time, 'DD MMM YY HH:mm') : '-'
    },
    addNewSubTenancyUser () {
      const accounts = Object.assign([], this.allAccounts)
      accounts.push(Object.assign({}, lodash.cloneDeep(accountModel)))
      this.updateTenancyAccounts({accounts})
    },
    deleteTenancyUser (index) {
      const accounts = lodash.cloneDeep(this.allAccounts)
      accounts.splice(index, 1)
      this.updateTenancyAccounts({accounts})
    },
    async saveMasterContact () {
      this.editing = false
      await this.uploadTenancyMasterAccount({tenancyId: this.tenancyId, account: lodash.set(this.masterAccount, 'perms', TenancySubAccountPerms)})
    },
    changeMasterAccountName (key, val) {
      this.masterAccount.TenancyAccount[key] = val
      this.updateAdminAccount(['TenancyAccount', key], val)
    },
    changeMasterAccountUsername (val) {
      this.masterAccount.username = val
      this.updateAdminAccount(['username'], val)
    },
    updateAdminAccount (path, val) {
      if (this.currentTenancy && this.currentTenancy.accounts) {
        const index = this.currentTenancy.accounts.findIndex(account => {
          return lodash.get(account, 'TenancyAccount.isAdmin')
        })
        if (index >= 0) {
          this.updateTenancyPathField({
            path: ['accounts', index, ...path],
            newValue: val,
          })
        }
      }
    },
    changeSubAccountUserName (item, val) {
      item.username = val
      delete item.id
      this.updateTenancyAccounts({ accounts: this.allAccounts })
    },
    changeSubAccountName (item, key, val) {
      item.TenancyAccount[key] = val
      this.updateTenancyAccounts({ accounts: this.allAccounts })
    },
    changePerms (item, permKey) {
      if (item.TenancyAccount.perms[permKey]) {
        delete item.TenancyAccount.perms[permKey]
      } else {
        item.TenancyAccount.perms[permKey] = TenancySubAccountPerms[permKey]
      }
    },
    async inviteSubAccount (account) {
      await activateSubAccountAndSendEmail(this.tenancyId, account)
      await this.loadTenancy(this.tenancyId)
    },
    async initModal () {
      this.deleteUserManagementCommitModal = this.$refs.deleteUserManagementCommitModal
    },
    resetPassword () {
      this.$router.push({
        name: 'resetContractorPwd',
      })
    },
    getFullName (item) {
      return this.displayI18nFullName(
        lodash.get(item, 'TenancyAccount.contactFirstName', ''),
        lodash.get(item, 'TenancyAccount.contactLastName', ''),
      )
    },
    async doInviteMasterAccount ({ masterFloors }) {
      const displayName = this.getFullName(this.masterAccount)
      const user = await tenanciesApi.inviteTenantContactUser(
        this.tenancyId,
        {
          username: this.masterAccount.username,
          ccdContactId: this.masterAccount.id,
          mainContactName: displayName,
          companyId: this.currentCompany.id,
          name: this.currentCompany.name,
          floor: masterFloors,
        },
      )
      await sendEmail(EmailTypes.tenancies.inviteTenantMasterUser, {
        to: this.masterAccount.username,
        displayName: displayName,
        contactFirstName: this.masterAccount.TenancyAccount.contactFirstName,
        contactLastName: this.masterAccount.TenancyAccount.contactLastName,
        inviteUserId: this.masterAccount.TenancyAccount.userId,
        needResetPwdLink: true,
      })
      if (masterFloors.length) {
        EventBus.emit('update_histories_table')
      }
      if (user) {
        await this.loadTenancy(this.tenancyId)
        notification.fireNotification(true, this.$i18n.t('success'))
      }
      this.closeMasterInviteModal()
    },
    isAdmin (item) {
      return lodash.get(item, 'TenancyAccount.isAdmin', false)
    },
    showDeleteModal (index) {
      this.editIndex = index
      this.deleteUserManagementCommitModal.open()
    },
    closeDeleteModal () {
      this.deleteUserManagementCommitModal && this.deleteUserManagementCommitModal.close()
    },
    deleteAccount () {
      this.deleteTenancyUser(this.editIndex)
      this.closeDeleteModal()
    },
    async sendRequest () {
      const results = await sendEmail(EmailTypes.tenancies.requestForInvitation, { inviteUserId: this.masterAccount.TenancyAccount.userId })
      if (results[0].isSuccess) {
        notification.fireNotification(true, this.$t('success'))
      }
    },
  },
}
</script>

<style lang="scss">
@import '~styles/popup';
.user-management-container {
  position: relative;
}
.user-management {
  background: #FAFBFC;
  overflow-x: auto;
  .sticky-state.sticky-btns {
    text-align: right;
    button {
      margin-right: 30px;
    }
  }
  .row.text-input {
    margin: 0;
  }
  .page-head {
    font-size: 0;
    button:first-child {
      margin-right: 15px;
    }
  }

  .user-management-content {
    height: 120px;
    padding: 20px 10px 0;
    background: #FAFBFC;
    label {
      color: #083e62;
      margin: 0;
      font-weight: bold;
    }
    & > label {
      width: 300px;
      height: 24px;
      line-height: 24px;
      font-family: Verdana;
      font-size: 20px;
      text-align: left;
    }
    .master-account {
      display: inline-flex;
      font-size: 0;
      padding-left: 20px;
      margin-top: 10px;
      .name {
        display: flex;
        .input-field:last-child{
          label{
            color: #FAFBFC;
          }
        }
        input {
          width: 100%;
        }
        .input-field:first-child span {
          margin-right: 5px;
        }
      }
      .name, .account-email {
        font-size: 14px;
      }
      .account-email {
        margin-left: 25px;
        input {
          width: 220px;
        }
      }
    }
    div.btns {
      display: inline-block;
      font-size: 0;
      vertical-align: bottom;
      margin-left: 80px;
      button {
        font-size: 14px;
      }
      button:first-child {
        margin-right: 15px;
      }
    }
  }

  .show-all-users {
    width: 100%;
    padding: 10px;
    & > label {
      font-size: 20px;
      color: #083e62;
      font-weight: bold;
    }
    table {
      border-spacing: 0px;
      border-collapse: collapse;
      thead, tbody {
        color: #083e62
      }
      th {
        text-align: center;
      }
      th:nth-child(1),
      th:nth-child(2){
        text-align: left;
      }
      th:last-of-type {
        min-width: 80px;
      }
      tr:nth-child(even) {
        background-color: #f8f8f8;
      }
      td.username {
        min-width: 200px;
      }
      td.name {
        min-width: 150px;
        .editing-name {
          font-size: 0;
        }
      }

      td:last-child {
        position: relative;
        i.fa-times {
          cursor: pointer;
          position: absolute;
          right: 5px;
          top: 15px;
        }
      }
      td:nth-child(0),td:nth-child(1) {
        text-align: left;
      }
      td {
        text-align: center;
        height: 45px;
      }
      td.lastLoginAt {
        width: 120px;
      }
      td.invitedAt{
        width: 130px;
        position: relative;
        div {
          width: 100%;
        }
        i {
          position: absolute;
          top: calc(50% - 8px);
          right: 0;
          cursor: pointer;
        }
      }
      button.invite {
        height: 26px;
        padding: 0 10px;
        line-height: 26px;
      }
      td.name {
        font-size: 0;
        text-align: left;
        div {
          font-size: 14px;
        }
        div.input-field {
          font-size: 14px;
          width: 50%;
          display: inline-block;
          input {
            width: 100%;
          }
        }
      }
    }
    .addBtn {
      border-bottom: 1px solid #0C3A58;
      cursor: pointer;
    }
  }

  .sticky-btns {
    position: absolute;
    top: -160px;
    right: -34px;
    font-size: 0;
    button {
      margin-right: 10px;
    }
  }
  .error-highlight {
    background-color: #f9dcda;
  }
}

.modal-pure-text ~ .modal-pure-text:last-child {
  margin-bottom: 0;
}
.modal-pure-text .modal-select {
  display: inline-block;
  width: 180px;
}

</style>
